import { useState } from 'react';
import { 
  Github, 
  ExternalLink, 
  Brain, 
  Globe, 
  Smartphone, 
  Code,
  ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Reveal } from '../hooks/useReveal.tsx';
import { TiltCard, TiltCardContent, TiltCardFloating } from './TiltCard.tsx';
import { MeshText } from './MeshText';

interface Project {
  icon: React.ElementType;
  title: string;
  description: string;
  tech: string[];
  status: 'Completed' | 'Developing';
  links: {
    github: string;
    demo?: string;
  };
  featured?: boolean;
}

const projects: Project[] = [
  {
    icon: Code,
    title: 'Algorithm Visualizer',
    description: 'Interactive tool for visualizing sorting and pathfinding algorithms, designed to help understand complex algorithms through visual representation.',
    tech: ['React', 'TypeScript', 'shadcn/ui', 'Express', 'PostgreSQL'],
    status: 'Completed',
    links: {
      github: 'https://github.com/Paras1209/Algorithm-Visualiser',
      demo: 'https://visualx.onrender.com/',
    },
    featured: true,
  },
  {
    icon: Brain,
    title: 'AI Image Generator',
    description: 'A web app that generates images using machine learning models and neural networks.',
    tech: ['Python', 'TensorFlow', 'React', 'Flask'],
    status: 'Developing',
    links: {
      github: 'https://github.com/Paras1209/Image-Generator',
    },
  },
  {
    icon: Smartphone,
    title: 'Uber Clone',
    description: 'A ride-sharing application clone with real-time tracking and route mapping functionality.',
    tech: ['React', 'MongoDB', 'Redux', 'Node.js'],
    status: 'Developing',
    links: {
      github: 'https://github.com/Paras1209/uber-clone',
    },
  },
  {
    icon: Smartphone,
    title: 'Weather App',
    description: 'Real-time weather forecasting with location tracking and beautiful UI.',
    tech: ['React Native', 'Weather API', 'Geolocation'],
    status: 'Completed',
    links: {
      github: 'https://github.com/Paras1209/Nature-Mood',
    },
  },
  {
    icon: Globe,
    title: 'Color Picker',
    description: 'An intuitive color picker application with various color format outputs.',
    tech: ['React', 'Vite'],
    status: 'Completed',
    links: {
      github: 'https://github.com/Paras1209/Color_picker',
      demo: 'https://color-picker-demo.netlify.app',
    },
  },
];

// Project card component with 3D tilt effect
function ProjectCard({ project, index }: { project: Project; index: number }) {
  const IconComponent = project.icon;
  
  return (
    <Reveal delay={450 + index * 80}>
      <TiltCard 
        className="h-full" 
        maxTilt={12} 
        scale={1.03}
        glareMaxOpacity={0.1}
      >
        <div className="group h-full rounded-xl border border-white/15 bg-white/10 backdrop-blur-md p-5 sm:p-6 transition-colors duration-300 hover:border-white/25">
          {/* Header */}
          <TiltCardFloating elevation={30}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <motion.div 
                  className="p-2 rounded-lg bg-white/15 group-hover:bg-white/20 transition-colors"
                  whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.5 } }}
                >
                  <IconComponent size={18} className="text-white/80" />
                </motion.div>
                <div className="h-[22px] w-[180px]">
                  <MeshText
                    text={project.title}
                    color="#ffffff"
                    fontFamily="Inter"
                    fontWeight={500}
                    fontSize={18}
                    colorSplit={true}
                    splitColors={['#61dafb', '#ff40c0']}
                    force={12}
                  />
                </div>
              </div>
              <span
                className={`mono-label px-2 py-1 rounded ${
                  project.status === 'Completed'
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}
              >
                {project.status === 'Completed' ? 'Done' : 'WIP'}
              </span>
            </div>
          </TiltCardFloating>
          
          {/* Description */}
          <TiltCardContent elevation={15}>
            <p className="text-sm text-white/70 mb-4 line-clamp-2">
              {project.description}
            </p>
          </TiltCardContent>
          
          {/* Tech */}
          <TiltCardContent elevation={20}>
            <div className="flex flex-wrap gap-2 mb-4">
              {project.tech.slice(0, 3).map((tech) => (
                <motion.span
                  key={tech}
                  className="px-2 py-0.5 text-xs rounded bg-white/5 text-white/60 border border-white/10"
                  whileHover={{ 
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'rgba(255,255,255,0.9)',
                    transition: { duration: 0.2 }
                  }}
                >
                  {tech}
                </motion.span>
              ))}
              {project.tech.length > 3 && (
                <span className="px-2 py-0.5 text-xs text-white/40">
                  +{project.tech.length - 3}
                </span>
              )}
            </div>
          </TiltCardContent>
          
          {/* Links */}
          <TiltCardFloating elevation={35}>
            <div className="flex gap-4 pt-2 border-t border-white/10">
              <motion.a
                href={project.links.github}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
                whileHover={{ x: 3 }}
              >
                <Github size={14} />
                Code
              </motion.a>
              {project.links.demo && (
                <motion.a
                  href={project.links.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
                  whileHover={{ x: 3 }}
                >
                  <ExternalLink size={14} />
                  Demo
                </motion.a>
              )}
            </div>
          </TiltCardFloating>
        </div>
      </TiltCard>
    </Reveal>
  );
}

// Featured project card with enhanced 3D effect
function FeaturedProjectCard({ project }: { project: Project }) {
  const IconComponent = project.icon;
  
  return (
    <TiltCard 
      maxTilt={8} 
      scale={1.02}
      glareMaxOpacity={0.12}
      perspective={1200}
    >
      <div className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md overflow-hidden">
        <div className="grid md:grid-cols-2 gap-6 p-6 sm:p-8">
          {/* Content */}
          <div className="flex flex-col justify-center">
            <TiltCardFloating elevation={40}>
              <div className="flex items-center gap-3 mb-4">
                <motion.div 
                  className="p-2 rounded-lg bg-white/15"
                  animate={{ 
                    boxShadow: [
                      '0 0 0 0 rgba(255,255,255,0.1)',
                      '0 0 20px 5px rgba(255,255,255,0.1)',
                      '0 0 0 0 rgba(255,255,255,0.1)'
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <IconComponent size={20} className="text-white" />
                </motion.div>
                <span className="mono-label text-white/60">Featured Project</span>
              </div>
            </TiltCardFloating>
            
            <TiltCardContent elevation={25}>
              <div className="h-[35px] sm:h-[42px] lg:h-[50px] w-full max-w-[400px] mb-3">
                <MeshText
                  text={project.title}
                  color="#ffffff"
                  fontFamily="Inter"
                  fontWeight={500}
                  fontSize={36}
                  colorSplit={true}
                  splitColors={['#61dafb', '#ff40c0']}
                  force={16}
                />
              </div>
              
              <p className="text-white/70 mb-6 leading-relaxed">
                {project.description}
              </p>
            </TiltCardContent>
            
            {/* Tech Stack */}
            <TiltCardContent elevation={30}>
              <div className="flex flex-wrap gap-2 mb-6">
                {project.tech.map((tech, i) => (
                  <motion.span
                    key={tech}
                    className="px-3 py-1 text-xs rounded-full bg-white/10 text-white/80 border border-white/10"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    whileHover={{ 
                      scale: 1.1,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                    }}
                  >
                    {tech}
                  </motion.span>
                ))}
              </div>
            </TiltCardContent>
            
            {/* Links */}
            <TiltCardFloating elevation={45}>
              <div className="flex gap-4">
                <motion.a
                  href={project.links.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                  whileHover={{ scale: 1.05, x: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Github size={16} />
                  <span className="text-sm">View Code</span>
                </motion.a>
                {project.links.demo && (
                  <motion.a
                    href={project.links.demo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ExternalLink size={16} />
                    <span className="text-sm">Live Demo</span>
                  </motion.a>
                )}
              </div>
            </TiltCardFloating>
          </div>
          
          {/* Image */}
          <div className="order-first md:order-last">
            <TiltCardFloating elevation={50}>
              <motion.div 
                className="rounded-xl overflow-hidden bg-white/5 aspect-video"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="/VisualX.png"
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </TiltCardFloating>
          </div>
        </div>
      </div>
    </TiltCard>
  );
}

export function ProjectsSection() {
  const [filter, setFilter] = useState<'all' | 'completed' | 'developing'>('all');
  
  const featuredProject = projects.find(p => p.featured);
  const otherProjects = projects.filter(p => !p.featured);
  
  const filteredProjects = filter === 'all' 
    ? otherProjects 
    : otherProjects.filter(p => p.status.toLowerCase() === filter);

  return (
    <section
      id="projects"
      className="flex flex-col px-5 sm:px-8 md:px-12 py-16 sm:py-20"
    >
      {/* Header */}
      <div className="flex flex-col gap-8 sm:flex-row sm:justify-between mb-12">
        <Reveal delay={120}>
          <div className="accent-badge px-3 py-1.5 rounded-r-md inline-block">
            <span className="mono-label-sm text-white/90">
              Projects
            </span>
          </div>
        </Reveal>

        <Reveal delay={220} className="max-w-sm sm:text-right">
          <p className="text-lg sm:text-xl leading-relaxed text-white text-shadow">
            A collection of projects showcasing my learning journey and technical growth.
          </p>
        </Reveal>
      </div>

      {/* Featured Project */}
      {featuredProject && (
        <Reveal delay={300}>
          <div className="mb-12">
            <FeaturedProjectCard project={featuredProject} />
          </div>
        </Reveal>
      )}

      {/* Filter Controls */}
      <Reveal delay={400}>
        <div className="flex justify-center gap-3 mb-8">
          {[
            { value: 'all', label: 'All' },
            { value: 'completed', label: 'Completed' },
            { value: 'developing', label: 'In Progress' },
          ].map((option) => (
            <motion.button
              key={option.value}
              onClick={() => setFilter(option.value as typeof filter)}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm transition-all duration-300 ${
                filter === option.value
                  ? 'bg-white text-black'
                  : 'bg-white/10 text-white/70 hover:bg-white/20 border border-white/10'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {option.label}
            </motion.button>
          ))}
        </div>
      </Reveal>

      {/* Project Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6">
        {filteredProjects.map((project, index) => (
          <ProjectCard key={project.title} project={project} index={index} />
        ))}
      </div>

      {/* Bottom CTA */}
      <Reveal delay={700}>
        <div className="text-center mt-12">
          <p className="text-white/50 text-sm mb-4">More projects coming soon</p>
          <motion.a
            href="https://github.com/Paras1209"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary rounded-full px-6 py-2.5 text-sm inline-flex items-center gap-2"
            whileHover={{ scale: 1.05, gap: '12px' }}
            whileTap={{ scale: 0.95 }}
          >
            <Github size={16} />
            View All on GitHub
            <ChevronRight size={14} />
          </motion.a>
        </div>
      </Reveal>
    </section>
  );
}
